import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { recordCapGateLogin } from '../../services/metrics';
import { trackEvent } from '../../services/analytics';
import './CapGate.css';

// Marker sent to the OAuth start endpoint so the callback can attribute the
// login (and any resulting signup) back to this gate. Must match
// OAUTH_SRC_CAP_GATE in backend/accounts/models.py.
const LOGIN_SRC = 'cap_gate';

/** Hard cap reached — generating again requires a login. */
export const VARIANT_GATE = 'gate';
/** Friendly suggestion after a few guest generations — blocks nothing. */
export const VARIANT_NUDGE = 'nudge';

const TXT = {
    title: {
        fr: 'Limite invité atteinte',
        en: "You've hit the guest limit",
        de: 'Gastlimit erreicht',
        es: 'Límite de invitado alcanzado',
        pt: 'Limite de convidado atingido',
    },
    nudgeTitle: {
        fr: 'Se connecter',
        en: 'Log in',
        de: 'Anmelden',
        es: 'Iniciar sesión',
        pt: 'Entrar',
    },
    usedDay: {
        fr: (n) => `Tu as atteint la limite de ${n} générations par jour sans compte.`,
        en: (n) => `You've hit the limit of ${n} generations per day without an account.`,
        de: (n) => `Du hast das Limit von ${n} Generierungen pro Tag ohne Konto erreicht.`,
        es: (n) => `Has alcanzado el límite de ${n} generaciones por día sin cuenta.`,
        pt: (n) => `Você atingiu o limite de ${n} gerações por dia sem conta.`,
    },
    usedWeek: {
        fr: (n) => `Tu as atteint la limite de ${n} générations par semaine sans compte.`,
        en: (n) => `You've hit the limit of ${n} generations per week without an account.`,
        de: (n) => `Du hast das Limit von ${n} Generierungen pro Woche ohne Konto erreicht.`,
        es: (n) => `Has alcanzado el límite de ${n} generaciones por semana sin cuenta.`,
        pt: (n) => `Você atingiu o limite de ${n} gerações por semana sem conta.`,
    },
    nudgeLead: {
        fr: (n) => `Tu as déjà lancé ${n} générations en invité aujourd'hui.`,
        en: (n) => `You've already run ${n} generations as a guest today.`,
        de: (n) => `Du hast heute bereits ${n} Generierungen als Gast gestartet.`,
        es: (n) => `Ya has lanzado ${n} generaciones como invitado hoy.`,
        pt: (n) => `Você já rodou ${n} gerações como convidado hoje.`,
    },
    // The cap is a bot ceiling, so say so: someone stopped by it should read it
    // as "the site is protecting itself", not "the site is selling me an
    // account". The perks below are the reason to log in; this is only why the
    // limit exists.
    pitch: {
        fr: 'Cette limite est là pour empêcher les bots de saturer le générateur. Connecte-toi avec Discord pour continuer — c\'est rapide.',
        en: 'This limit exists to keep bots from flooding the generator. Connect with Discord to keep going — it\'s quick.',
        de: 'Dieses Limit hält Bots davon ab, den Generator zu überlasten. Melde dich mit Discord an, um weiterzumachen — das geht schnell.',
        es: 'Este límite existe para evitar que los bots saturen el generador. Conéctate con Discord para continuar: es rápido.',
        pt: 'Este limite existe para impedir que bots sobrecarreguem o gerador. Conecte-se com o Discord para continuar — é rápido.',
    },
    nudgePitch: {
        fr: 'Se connecter est rapide, et te donne :',
        en: 'Logging in is quick, and gets you:',
        de: 'Die Anmeldung geht schnell und bringt dir:',
        es: 'Iniciar sesión es rápido y te da:',
        pt: 'Entrar é rápido e te dá:',
    },
    perkGens: {
        fr: 'Générations infinies',
        en: 'Unlimited generations',
        de: 'Unbegrenzte Generierungen',
        es: 'Generaciones ilimitadas',
        pt: 'Gerações ilimitadas',
    },
    perkSave: {
        fr: 'Stuffs sauvegardés sur ton compte',
        en: 'Builds saved to your account',
        de: 'Builds in deinem Konto gespeichert',
        es: 'Builds guardadas en tu cuenta',
        pt: 'Builds salvas na sua conta',
    },
    perkShare: {
        fr: 'Partage tes stuffs avec la communauté',
        en: 'Share your builds with the community',
        de: 'Teile deine Builds mit der Community',
        es: 'Comparte tus builds con la comunidad',
        pt: 'Compartilhe suas builds com a comunidade',
    },
    connect: {
        fr: 'Se connecter avec Discord',
        en: 'Connect with Discord',
        de: 'Mit Discord anmelden',
        es: 'Iniciar sesión con Discord',
        pt: 'Entrar com Discord',
    },
    later: {
        fr: 'Plus tard',
        en: 'Maybe later',
        de: 'Später',
        es: 'Quizás más tarde',
        pt: 'Talvez depois',
    },
    resets: {
        fr: 'Sinon, ton quota se réinitialise plus tard.',
        en: 'Otherwise your quota resets later.',
        de: 'Andernfalls wird dein Kontingent später zurückgesetzt.',
        es: 'Si no, tu cuota se reinicia más tarde.',
        pt: 'Caso contrário, sua cota é reiniciada mais tarde.',
    },
    nudgeNote: {
        fr: 'Rien ne t\'oblige : tu peux continuer à générer sans compte.',
        en: 'Nothing is blocked: you can keep generating without an account.',
        de: 'Nichts ist blockiert: Du kannst auch ohne Konto weiter generieren.',
        es: 'Nada está bloqueado: puedes seguir generando sin cuenta.',
        pt: 'Nada está bloqueado: você pode continuar gerando sem conta.',
    },
};

const t = (key, lang) => TXT[key][lang] || TXT[key].en;

/** Overlay heading for either variant — the popup's own copy owns both. */
export const capGateTitle = (lang, variant = VARIANT_GATE) =>
    t(variant === VARIANT_NUDGE ? 'nudgeTitle' : 'title', lang);

/**
 * The log-in popup shown around the guest generation quota, in two variants.
 *
 * `gate` is the hard stop: the backend answered a generate request with
 * `login_required` because the anti-bot cap is reached, and nothing more
 * generates until the visitor logs in. `nudge` is the soft one, raised by the
 * frontend after a handful of guest generations — same pitch, same perks, but
 * dismissing it costs nothing and the run it follows already succeeded.
 *
 * Both are modals with the Discord button in them rather than toasts: this is
 * the app's only conversion moment, so the action has to be one click from the
 * message. Only the gate feeds the cap-gate funnel (see services/metrics.js) —
 * mixing an unprompted suggestion into it would make conversion look worse
 * than it is by inflating the denominator.
 */
function CapGate({ lang = 'fr', variant = VARIANT_GATE, period = 'day', limit, count, nextPath, onDismiss }) {
    const { startLogin } = useAuth();
    const isNudge = variant === VARIANT_NUDGE;

    const handleConnect = () => {
        if (!isNudge) recordCapGateLogin();
        trackEvent(isNudge ? 'login_nudge_login' : 'cap_gate_login');
        startLogin(nextPath, LOGIN_SRC);
    };

    return (
        <div className="cap-gate">
            <p className="cap-gate-lead">
                {isNudge
                    ? t('nudgeLead', lang)(count)
                    : t(period === 'week' ? 'usedWeek' : 'usedDay', lang)(limit)}
            </p>
            <p className="cap-gate-pitch">{t(isNudge ? 'nudgePitch' : 'pitch', lang)}</p>

            <ul className="cap-gate-perks">
                <li>{t('perkGens', lang)}</li>
                <li>{t('perkSave', lang)}</li>
                <li>{t('perkShare', lang)}</li>
            </ul>

            <div className="cap-gate-actions">
                <button type="button" className="cap-gate-later" onClick={onDismiss}>
                    {t('later', lang)}
                </button>
                <button type="button" className="primary alt" onClick={handleConnect}>
                    {t('connect', lang)}
                </button>
            </div>

            <p className="cap-gate-note">{t(isNudge ? 'nudgeNote' : 'resets', lang)}</p>
        </div>
    );
}

export default CapGate;
